import { getQuestionPlan, type PlanId } from '../data/plans';
import { getStoredToken } from './api';

type CheckoutSessionResponse = {
  checkout_url?: unknown;
  session_id?: unknown;
};

export class CheckoutUnavailableError extends Error {
  constructor() {
    super('Secure checkout is not available in this local preview yet.');
    this.name = 'CheckoutUnavailableError';
  }
}

function checkoutEndpoint(): string | null {
  const base = String(import.meta.env.VITE_API_URL || '').trim().replace(/\/$/, '');
  if (!base || import.meta.env.VITE_CHECKOUT_ENABLED !== 'true') return null;
  return `${base}/sanjeom-ai/checkout`;
}

function createCheckoutKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `checkout-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export async function createQuestionPackCheckout(
  planId: PlanId,
  clientCheckoutKey = createCheckoutKey(),
): Promise<string> {
  const plan = getQuestionPlan(planId);
  const endpoint = checkoutEndpoint();
  if (!plan || !endpoint) throw new CheckoutUnavailableError();
  const token = getStoredToken();
  if (!token) throw new Error('Sign in before starting checkout.');

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      plan_id: plan.id,
      client_checkout_key: clientCheckoutKey,
      return_url: window.location.origin,
    }),
  });

  if (!response.ok) {
    throw new Error('We could not start checkout. Please try again.');
  }

  const payload = await response.json() as CheckoutSessionResponse;
  const checkoutUrl = String(payload.checkout_url || '').trim();
  if (!checkoutUrl.startsWith('https://checkout.stripe.com/')) {
    throw new Error('We could not start checkout. Please try again.');
  }
  return checkoutUrl;
}

export async function confirmQuestionPackCheckout(sessionId: string): Promise<{
  status: string;
  confirmed: boolean;
  questions_remaining: number;
}> {
  const base = String(import.meta.env.VITE_API_URL || '').trim().replace(/\/$/, '');
  const token = getStoredToken();
  if (!base || !token) throw new Error('Sign in to confirm this payment.');
  const response = await fetch(`${base}/sanjeom-ai/checkout/confirm`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ session_id: sessionId }),
  });
  if (!response.ok) throw new Error('Payment confirmation is still processing. Please try again shortly.');
  return await response.json() as {
    status: string;
    confirmed: boolean;
    questions_remaining: number;
  };
}
